export type CurrencyCode =
  | 'INR'
  | 'USD'
  | 'EUR'
  | 'GBP'
  | 'AED'
  | 'SAR'
  | 'JPY'
  | 'CNY'
  | 'AUD'
  | 'CAD'
  | 'SGD'
  | 'CHF'
  | 'HKD'
  | 'NZD'
  | 'ZAR'

export const CURRENCY_OPTIONS: { code: CurrencyCode; label: string }[] = [
  { code: 'INR', label: 'INR — Indian Rupee' },
  { code: 'USD', label: 'USD — US Dollar' },
  { code: 'EUR', label: 'EUR — Euro' },
  { code: 'GBP', label: 'GBP — British Pound' },
  { code: 'AED', label: 'AED — UAE Dirham' },
  { code: 'SAR', label: 'SAR — Saudi Riyal' },
  { code: 'JPY', label: 'JPY — Japanese Yen' },
  { code: 'CNY', label: 'CNY — Chinese Yuan' },
  { code: 'AUD', label: 'AUD — Australian Dollar' },
  { code: 'CAD', label: 'CAD — Canadian Dollar' },
  { code: 'SGD', label: 'SGD — Singapore Dollar' },
  { code: 'CHF', label: 'CHF — Swiss Franc' },
  { code: 'HKD', label: 'HKD — Hong Kong Dollar' },
  { code: 'NZD', label: 'NZD — New Zealand Dollar' },
  { code: 'ZAR', label: 'ZAR — South African Rand' },
]

function localeFor(currency: CurrencyCode): string {
  return currency === 'INR' ? 'en-IN' : 'en-US'
}

export function formatCurrency(amount: number, currency: CurrencyCode = 'INR'): string {
  return new Intl.NumberFormat(localeFor(currency), {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(amount)
}

// For glanceable summaries on narrow screens; exact amounts keep formatCurrency.
export function formatCurrencyWhole(amount: number, currency: CurrencyCode = 'INR'): string {
  return new Intl.NumberFormat(localeFor(currency), {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount)
}

export function formatCurrencyNoZeroPaise(amount: number, currency: CurrencyCode = 'INR'): string {
  return Number.isInteger(Math.round(amount * 100) / 100)
    ? formatCurrencyWhole(amount, currency)
    : formatCurrency(amount, currency)
}

export function currencySymbol(currency: CurrencyCode = 'INR'): string {
  const parts = new Intl.NumberFormat(localeFor(currency), {
    style: 'currency',
    currency,
  }).formatToParts(0)
  return parts.find((p) => p.type === 'currency')?.value ?? currency
}

export function formatCurrencyCompact(amount: number, currency: CurrencyCode = 'INR'): string {
  const compact = new Intl.NumberFormat(localeFor(currency), {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(amount)
  return `${currencySymbol(currency)}${compact}`
}

export type AmountToneKind = 'income' | 'expense' | 'auto'

// Green = gain, red = loss — the finance-domain convention already established in the
// Transactions list (loss aversion research: red reads as more severe than other colors,
// which is desirable for spending awareness). 'income'/'expense' are fixed by what the
// figure represents; 'auto' is for figures that can flip sign (net worth, savings).
// formatCurrency already renders a leading "-" for negatives, so color is never the only
// signal distinguishing a shortfall.
export function amountToneClass(kind: AmountToneKind, value: number): string {
  if (kind === 'income') return 'text-[var(--fl-income)]'
  if (kind === 'expense') return 'text-[var(--fl-error)]'
  return value >= 0 ? 'text-[var(--fl-income)]' : 'text-[var(--fl-error)]'
}
